// Utilities extracted from UnitPlanModule.js

import _ from "lodash";
import React from "react";
import {
  generateRandomId,
  getArchivedSortedSubjectListMemoized,
  getMemoizedLangFunction,
} from "Utils";
import {
  getSubjectGroupsFromCache,
  getTemplateBasicDetailsOfGradeFromCache,
  uniquelyCombineGradeDetails,
  getUniquelyCombinedGradesDetails,
} from "../UnitPlanGraphqlHelpers";
import { getOrganizationCurriculumPrograms } from "modules/CommonSelectors";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getLearningCourseDetailsFromCache } from "LearningCourse/modules/LearningCourseGraphqlHelpers";
import { getLearningCourseVariables } from "Teacher/routes/LearningCourse/modules/Utils";
import { getUnitPlanFieldFromCache } from "IBPlanner/modules/IBGraphqlHelpers";
import {
  getUnitPlanFieldResponseFilter,
  getLearningCourseSubjects,
  getElementValue,
  getSubjectsFromDataSetNodeWrapper,
  getElementLabel,
} from "UnitPlans/modules/Utils";
import {
  getStartEndDatesOfAcademicYears,
  getSubjectLabelAndIsArchived,
} from "modules/Services/ServicesUtils";
import {
  pypElementMapping,
  pypElementSetMapping,
} from "Constants/plannerElementMappings";
import { UNIT_TYPE_LOCALE } from "Constants";
import moment from "moment";
import ACLStore from "lib/aclStore";
import {
  CURRICULUM_TYPE_DP,
  CURRICULUM_TYPE_MYP,
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_UBD,
  DP_SUBJECT_SPECIFIC,
  MYP_INTERDISCIPLINARY,
  MYP_SUBJECT_SPECIFIC,
  PYP_SUBJECT_SPECIFIC,
  PYP_TRANSDISCIPLINARY,
  SUBJECT_TYPE_GENERIC,
  UBD_INQUIRY,
  UBD_SUBJECT_SPECIFIC,
  ELEMENT_TYPE_PYP_LEARNING_STANDARD,
  ELEMENT_TYPE_PYP_ATL,
} from "Constants/stringConstants";
import { getFieldLockedObject } from "modules/PusherModule/PusherUtils";
import {
  getSelectedAcademicYearId,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { isSinglePageAndNewUnitView } from "UnitPlans/modules/UnitViewModeUtils";
import { getIsLearningCoursesEnabled } from "Teacher/routes/LearningCourses/modules/Utils";
import { getLearningCourseTermDates } from "UnitPlans/modules/LearningCourseFlow/Utils";
import { ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION } from "Constants/permissionConstants";

const shouldConvertNonPortedFieldsToPorted = () => {
  return ACLStore.can("FeatureFlag:EnablePortedFieldsForUnitTemplate");
};

export const ResourceDetailsContext = React.createContext();

export const getPlannerFieldMutationParams = ({
  oldValue,
  fieldUID,
  newValue,
  id,
  hasAddRemoveValues,
  valuesToAdd,
  valuesToRemove,
}) => {
  const params = { id, value: newValue, fieldUID };
  params["hasAddRemoveValues"] = hasAddRemoveValues;
  if (hasAddRemoveValues) {
    params["valuesToAdd"] = valuesToAdd ?? _.difference(newValue, oldValue);
    params["valuesToRemove"] =
      valuesToRemove ?? _.difference(oldValue, newValue);
  } else {
    params["value"] = newValue;
  }
  return params;
};

export const attachmentObj = {
  id: generateRandomId(),
  name: "",
  type: "",
  mimeType: "",
  url: "",
  thumbUrl: "",
  title: "",
  metadata: "",
  __typename: "Attachment",
};

export const enityEnumToLocalTypeMapping = {
  UNIT_PLAN: "unitPlan",
  ASSESSMENT: "assessment",
  LEARNING_ENGAGEMENT: "le",
};

/**
 * Filters planner element node sets by removing specific types
 * @param {Array} plannerElementNodeSets - Array of planner element node sets to filter
 * @param {Array} additionalTypesToRemove - Additional types to remove beyond the default list
 * @returns {Array} Filtered array of planner element node sets
 */
export const getFilteredPlannerElementNodeSets = ({
  plannerElementNodeSets,
  additionalTypesToRemove = [],
}) => {
  return _.filter(plannerElementNodeSets, nodeSet => {
    return !_.includes(additionalTypesToRemove, nodeSet.type);
  });
};

const PLANNER_ELEMENT_TYPES_V2 = ["CARD", "CHECKLIST", "NESTED_CHECKLIST"];
export const VISIBLE_ELEMENT_TYPES_FOR_V2 = ["CARD", "CHECKLIST"];

export const getAllPlannerElementTypesFromFields = ({ fieldList }) => {
  const shouldConvertNonPortedFieldsToPortedFlag = shouldConvertNonPortedFieldsToPorted();
  const plannerElementTypesSet = _.reduce(
    fieldList,
    (result, field) => {
      const plannerElementType = _.get(
        field,
        shouldConvertNonPortedFieldsToPortedFlag
          ? "plannerElementConfigType.value"
          : "config.plannerElementConfig.type",
        ""
      );
      if (plannerElementType) {
        result.add(plannerElementType);
      }
      return result;
    },
    new Set()
  );

  return _.concat(Array.from(plannerElementTypesSet), PLANNER_ELEMENT_TYPES_V2);
};

/**
 * Merges planner element node sets, ensuring new fragments are properly merged
 * Uses deep merge to combine properties of node sets with the same ID
 * @param {Array} existingNodeSets - Existing node sets
 * @param {Array} newNodeSets - New node sets to merge in
 * @returns {Array} Merged array of planner element node sets
 */
export const getMergedPlannerElementNodeSets = (
  existingNodeSets = [],
  newNodeSets = []
) => {
  const mergedNodeSetsMap = _.keyBy(existingNodeSets, "id");
  _.forEach(newNodeSets, nodeSet => {
    mergedNodeSetsMap[nodeSet.id] = _.merge(
      {},
      mergedNodeSetsMap[nodeSet.id] || {},
      nodeSet
    );
  });

  return _.values(mergedNodeSetsMap);
};

export const getBaseCurriculumProgram = (
  state,
  { currentCurriculumProgramId, currentCurriculumProgramType }
) =>
  _.find(
    getOrganizationCurriculumPrograms(state),
    curr =>
      curr.id !== currentCurriculumProgramId &&
      curr.type === currentCurriculumProgramType &&
      curr.isPrimary
  );

export const getSortedSubjectLevelTags = ({
  organizationSubjectLevels,
  selectedTagList,
}) => {
  return _.intersectionBy(organizationSubjectLevels, selectedTagList, "id");
};

const fieldUids = {};

const getUnitPlanCourses = ({ unitPlanDetails }) => {
  return _.map(unitPlanDetails.courses, course => course.id);
};

export const getUnitPlanCoursesMemoized = _.memoize(
  params => getUnitPlanCourses(params),
  params => JSON.stringify(params)
);

//When the project or unitplan Date is before the timeline start date, We start the bar from timeline start date
//Similar condition in end date too
export const getUpdatedStartEndDates = ({
  startDate,
  endDate,
  timelineStartDate,
  timelineEndDate,
}) => {
  let updatedStartDate = startDate;
  let updatedEndDate = endDate;
  if (moment(updatedStartDate).isBefore(moment(timelineStartDate))) {
    updatedStartDate = timelineStartDate;
    if (moment(updatedEndDate).isBefore(moment(timelineStartDate))) {
      updatedEndDate = timelineStartDate;
    }
  }

  if (moment(updatedEndDate).isAfter(moment(timelineEndDate))) {
    updatedEndDate = timelineEndDate;
    if (moment(updatedStartDate).isAfter(moment(timelineEndDate))) {
      updatedStartDate = timelineEndDate;
    }
  }

  return {
    startDate: updatedStartDate,
    endDate: updatedEndDate,
  };
};

export const getFieldTypeConfig = ({ fieldTypeConfig, fieldKey, unitType }) => {
  switch (fieldKey) {
    // case "subjects":
    //   return {
    //     multi: _.includes(["ibInquiry", "ibMYPInterdisciplinary"], unitType)
    //   }; //Handle multi based on unitType wheather it is inquiry or standalone
    default:
      return fieldTypeConfig;
  }
};

export const getFieldObject = ({ field_list, fieldKey }) => {
  const fieldObject = _.find(field_list, (value, key) => {
    const fieldObject = field_list[key];

    const subFields = _.get(fieldObject, "subFields", []);

    return (
      key === fieldKey ||
      _.findIndex(subFields, ({ key }) => key === fieldKey) > -1
    );
  });

  const subFieldObject = _.find(
    _.get(fieldObject, "subFields", []),
    ({ key }) => key === fieldKey
  );

  if (_.isEmpty(subFieldObject)) {
    return fieldObject;
  }

  return subFieldObject;
};

export const getPlannerFieldOptions = ({
  fieldKey,
  organizationId,
  grades,
  unitPlanReduxData,
  defaultOptions,
  unitPlanFields,
  field_list,
  readOnly,
  plannerElements, //To fetch curriculum planner elements
  plannerElementNodeSets,
  curriculumType, //To handle curriculum specific condition
  courseId, //To fetch subject from course
  mode = "edit",
  objectiveLevelNodes = [], // get the levels of objectives for subjects
  selectedAcademicYear,
  courseSubjects,
  unitPlanCoursesSubjects,
  t,
  academicYears,
  genericTags,
  noMinMaxDates = false,
  fieldObj,
  curriculumId,
  subjectRecommendedPhaseMap,
  learningCourseId,
  isCourseOverViewAndViewMode,
  shouldGroupObjectivesBySubject = false,
}) => {
  let subjects = [];
  let subjectGroups = [];
  const academicYearId = _.get(selectedAcademicYear, "id", "");

  if (!_.isEmpty(unitPlanReduxData)) {
    unitPlanFields = _.map(unitPlanReduxData, (obj, key) => {
      return { uid: key, ...obj };
    });
  }

  const unitType = _.get(
    _.find(unitPlanFields, { uid: "unitType" }),
    "value",
    ""
  );

  fieldObj = fieldObj || getFieldObject({ field_list, fieldKey });

  const pypPlannerElementConfig = _.get(
    fieldObj,
    "config.plannerElementConfig",
    {}
  );
  switch (fieldKey) {
    case "dateDuration": {
      let academicYearStartDate, academicYearEndDate, minDate, maxDate;

      if (noMinMaxDates) {
        return;
      }

      const isLearningCoursesEnabled = getIsLearningCoursesEnabled();
      if (isLearningCoursesEnabled) {
        const { startDate, endDate } = getLearningCourseTermDates({
          learningCourseId,
          academicYearId,
        });
        minDate = moment(startDate).format("YYYY-MM-DD");
        maxDate = moment(endDate).format("YYYY-MM-DD");
        return {
          minDate,
          maxDate,
        };
      }
      if (!_.isEmpty(academicYears)) {
        const { startDate, endDate } = getStartEndDatesOfAcademicYears({
          academicYears,
        });

        minDate = moment(startDate).format("YYYY-MM-DD");
        maxDate = moment(endDate).format("YYYY-MM-DD");
      } else {
        if (!_.isNil(selectedAcademicYear)) {
          academicYearStartDate = selectedAcademicYear.startDate;
          academicYearEndDate = selectedAcademicYear.endDate;
        } else {
          const currentAcademicYear = getCurrentAcademicYear({
            organizationId,
          });
          academicYearStartDate = _.get(currentAcademicYear, "startDate");
          academicYearEndDate = _.get(currentAcademicYear, "endDate");
        }

        minDate = moment(academicYearStartDate).format("YYYY-MM-DD");
        maxDate = moment(academicYearEndDate).format("YYYY-MM-DD");
      }

      return {
        minDate,
        maxDate,
      };
    }
    case "subjects": {
      let subjectList = [];
      let courseDetails = {};
      const subjectListFromUnitPlan = _.get(
        _.find(unitPlanFields, {
          uid: "subjects",
        }),
        "resolvedMinimalTree.subjects",
        []
      );

      if (
        _.includes([CURRICULUM_TYPE_MYP, CURRICULUM_TYPE_DP], curriculumType)
      ) {
        if (courseId) {
          const courseGradeFilters = {
            academicYearId,
          };
          courseDetails = getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          });
          subjectList = _.get(courseDetails, "subjects", []);
        } else {
          subjectList = subjectListFromUnitPlan;
        }
      }
      if (curriculumType == CURRICULUM_TYPE_DP) {
        if (mode === "edit") {
          subjectList = subjectListFromUnitPlan;
        }
        getArchivedSortedSubjectListMemoized({ subjectList });
        return subjectList;
      } else if (curriculumType == CURRICULUM_TYPE_MYP) {
        const curriculumIdAsFilter = {
          curriculumProgramIds: curriculumId,
        };

        if (learningCourseId) {
          const learningCourseVariables = getLearningCourseVariables({
            learningCourseId,
            academicYearId,
          });

          courseDetails = getLearningCourseDetailsFromCache(
            learningCourseVariables
          );
        }

        const subjectGroupData = _.get(
          getSubjectGroupsFromCache({
            organizationId,
            filters: curriculumIdAsFilter,
          }),
          "subjectGroups",
          []
        );

        subjectGroups = _.map(subjectGroupData, group => {
          return {
            ...group,
            subjects: getArchivedSortedSubjectListMemoized({
              subjectList: _.get(group, "subjects", []),
            }),
          };
        });

        // Extract subjects from current class
        const initSubject = !_.isEmpty(subjectList) ? _.first(subjectList) : {};

        let objectiveLevelsData, selectedObjectiveLevels;
        const selectedObjectiveLevelsWithValue = [];

        const isObjectivePhasesEnabled = !ACLStore.can(
          "FeatureFlag:RemoveMYPObjectivesPhase"
        );

        if (isObjectivePhasesEnabled) {
          objectiveLevelsData =
            _.find(unitPlanFields, { uid: "objectiveLevel" }) || {};
          selectedObjectiveLevels = _.get(objectiveLevelsData, "value", "");

          const objectiveUidsWithSelectedValue = _.filter(
            unitPlanFields,
            field => {
              return (
                (_.startsWith(
                  _.get(field, "uid"),
                  "disciplinaryObjectivesMYP"
                ) ||
                  _.isEqual(_.get(field, "uid"), "objectivesMYP")) &&
                !_.isEmpty(field.value)
              );
            }
          );

          _.forEach(objectiveUidsWithSelectedValue, objective => {
            //for every subjects objective filtered out there selected objectiveids
            selectedObjectiveLevelsWithValue.push(
              ..._.map(
                //filtered out the selected objectives parent
                _.filter(
                  objective.resolvedMinimalTree.nodes,
                  node => node.depth == 1
                ),
                objectiveNode => objectiveNode.id
              )
            );
          });
        }

        return {
          courseDetails,
          subjectGroups,
          initSubject,
          isEditable: mode == "create",
          unitType,
          ...(isObjectivePhasesEnabled
            ? {
                objectiveLevelNodes,
                selectedObjectiveLevels,
                selectedObjectiveLevelsWithValue,
              }
            : {}),
        };
      } else {
        subjects = getSubjectList({
          grades,
          unitPlanFields,
          readOnly,
          learningCourseId,
          academicYearId,
        });
        if (
          curriculumType == CURRICULUM_TYPE_UBD &&
          unitType == UBD_SUBJECT_SPECIFIC
        ) {
          // filtering data of subjects with course mapped subjects
          if (
            !_.isEmpty(unitPlanCoursesSubjects) ||
            !_.isEmpty(courseSubjects)
          ) {
            subjects = _.filter(subjects, subject =>
              _.find(
                mode == "edit" || isSinglePageAndNewUnitView()
                  ? unitPlanCoursesSubjects
                  : courseSubjects,
                courseSubject => courseSubject.id == subject.id
              )
            );
          }
        }
        if (curriculumType === CURRICULUM_TYPE_PYP && mode === "create") {
          // filter the archived subjects and only show the active subjects
          subjects = _.filter(subjects, subject => !subject.isArchived);
        }
        const sortedSubjects = getArchivedSortedSubjectListMemoized({
          subjectList: subjects,
        });

        return getSubjectOptions({ subjects: sortedSubjects });
      }
    }
    case "subjectLevel": {
      let subjectVariant = [];
      if (
        !_.isEmpty(
          _.get(
            _.find(unitPlanFields, { uid: "subjects" }),
            "resolvedMinimalTree.subjects[0].flatVariants",
            []
          )
        )
      )
        subjectVariant = _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects[0].flatVariants",
          []
        );
      //In DP for subject level there will always 1 subject mapped to course so we are getting the data of 1st subject
      else {
        const academicYearId = _.get(selectedAcademicYear, "id", "");
        const courseGradeFilters = {
          academicYearId,
        };
        subjectVariant = _.get(
          getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters,
          }),
          "subjects[0].flatVariants",
          []
        );
      }

      const uniqueSubjectLevels = _.uniqBy(subjectVariant, function (e) {
        return e.level.id;
      });
      const subjectLevels = _.map(uniqueSubjectLevels, variant => {
        return {
          value: _.get(variant, "level.id", ""),
          label: _.get(variant, "level.label", ""),
          isArchived: _.get(variant, "isArchived", false),
        };
      });
      return subjectLevels;
    }
    case "benchmarks":
      return getBenchmarksOptions({
        unitPlanFields,
        grades,
      });
    case "relatedConcepts":
      return getRelatedConceptOptions({
        unitPlanFields,
        defaultOptions,
        plannerElementNodeSets,
      });
    case "teacherQues":
      return getTeacherQuestionsOptions({ unitPlanFields });
    case "focusAreas":
      return getFocusAreaOptions({
        unitPlanFields,
        readOnly,
        plannerElements,
        plannerElementNodeSets,
        pypPlannerElementConfig,
      });
    case "theme":
    case "concepts":
    case "action":
    case "lp":
      return getElementOptions({
        uid: fieldKey,
        unitPlanFields,
        readOnly,
        plannerElements,
        plannerElementNodeSets,
        pypPlannerElementConfig,
        fieldObj,
      });

    case "atls":
      return getAtlsOptions({
        organizationId,
        grades,
        unitPlanFields,
        readOnly,
        plannerElementNodeSets,
      });
    case "unitType":
      return getUnitTypeOptionsMemoized({
        grades,
        organizationId,
        curriculumType,
      });
    case "conceptsQuestion":
      return getElementQuesOptions({
        organizationId,
        fieldObj,
        unitPlanFields,
        uid: "concepts",
      });
    case "actionQuestion":
      return getElementQuesOptions({
        organizationId,
        fieldObj,
        unitPlanFields,
        uid: "action",
      });
    case "hours":
      return getHoursOptions();

    default: {
      const configType = _.get(fieldObj, "config.type", "");

      if (configType == "PLANNER_ELEMENT") {
        return getPlannerElementOptions({
          t,
          grades,
          fieldObj,
          plannerElements,
          plannerElementNodeSets,
          unitPlanFields,
          objectiveLevelNodes,
          fieldKey,
          genericTags,
          fieldList: field_list,
          courseId,
          subjectRecommendedPhaseMap,
          curriculumType,
          isCourseOverViewAndViewMode,
          shouldGroupObjectivesBySubject,
        });
      } else {
        const filteredBy = _.get(fieldObj, "config.filteredBy", []);

        if (!_.isEmpty(filteredBy)) {
          return getNonPlannerFieldOptions({
            filteredBy,
            unitPlanFields,
            defaultOptions,
            genericTags,
            objectiveLevelNodes,
            plannerElements,
            plannerElementNodeSets,
            fieldObj,
            t,
            grades,
            fieldList: field_list,
          });
        }

        return defaultOptions ? defaultOptions : [];
      }
    }
  }
};

const getNonPlannerFieldOptions = ({
  filteredBy,
  unitPlanFields,
  defaultOptions,
  genericTags,
  objectiveLevelNodes,
  plannerElements,
  plannerElementNodeSets,
  fieldObj,
  t,
  grades,
  fieldList,
}) => {
  const filteredNodes = [];

  let options = {};

  let resolvedMinimalTree = {};

  _.forEach(filteredBy, fieldKey => {
    const { type } = fieldList[fieldKey];

    switch (type) {
      case "NestedSelectComponent": {
        options = getPlannerElementOptions({
          t,
          grades,
          fieldObj,
          plannerElements,
          plannerElementNodeSets,
          unitPlanFields,
          objectiveLevelNodes,
          fieldKey,
          genericTags,
        });

        resolvedMinimalTree = _.get(
          _.find(unitPlanFields, ({ uid }) => uid == fieldKey),
          "resolvedMinimalTree",
          {}
        );

        break;
      }

      case "IBBenchmarks": {
        options = getBenchmarksOptions({ grades, unitPlanFields });

        resolvedMinimalTree = _.get(
          _.find(unitPlanFields, ({ uid }) => uid == fieldKey),
          `resolvedMinimalTree`,
          {}
        );

        break;
      }

      default: {
        const resolvedValue = _.get(
          _.find(unitPlanFields, { uid: fieldKey }),
          "resolvedMinimalTree.nodes",
          []
        );

        _.forEach(resolvedValue, node => {
          if (node) {
            filteredNodes.push({
              label: node.label,
              value: node.id,
              subText: node.subText,
            });
          }
        });
      }
    }
  });
  return {
    ...defaultOptions,
    nodes: filteredNodes,
    extraText: "",
    ...options,
    resolvedMinimalTree,
  };
};

export const getFieldLockedObjectForFieldUids = ({ key, lockedFields }) => {
  let uids = [];
  switch (key) {
    case "conceptsQuestion":
    case "concepts":
      uids = ["conceptsQuestion", "concepts"];
      break;

    default:
      uids = [key];
  }

  return getFieldLockedObject({ uids, lockedFields });
};

/*
Handle SubFields for Unit Plan, LE and Assessment.

@params
  fieldKey :"inquiryQuestions"
  fieldTemplateObj: field obj of template
  fieldDataObj: field data obj get from backend.
  unitPlanFieldDataObj: unitplan field data obj get from backend
*/

//Need Review @kunal
export const getSubFields = ({
  fieldKey,
  fieldTemplateObj,
  fieldDataObj,
  unitPlanFieldDataObj,
  isUnitPlan = false,
}) => {
  const subFields = _.get(fieldTemplateObj, "subFields", []);

  return _.reduce(
    subFields,
    (result, subField) => {
      const { key } = subField;

      let fieldValue = _.get(fieldDataObj, "value", {});
      const unitPlanFieldValue = _.get(unitPlanFieldDataObj, "value", {});
      fieldValue = getElementValue({
        fieldUID: fieldKey,
        valueKeys: fieldValue,
        unitPlanFieldValue,
        fieldObj: fieldTemplateObj,
        isUnitPlan,
      });

      const subFieldValue = fieldValue[key];

      if (!_.isEmpty(subFieldValue)) {
        result.push(subField);
      }

      return result;
    },
    []
  );
};

//Need Review @kunal
export const getPlannerElementOptions = ({
  fieldObj,
  plannerElements,
  plannerElementNodeSets,
  unitPlanFields,
  grades,
  genericTags,
  objectiveLevelNodes,
  t,
  fieldKey,
  fieldList,
  courseId,
  subjectRecommendedPhaseMap,
  curriculumType,
  isCourseOverViewAndViewMode = false,
  shouldGroupObjectivesBySubject = false,
}) => {
  const config = _.get(fieldObj, "config", {});
  const dynamicSubject = _.get(fieldObj, "dynamicSubject", {});
  const { isSubjectSpecificDynamic = false, isDisciplinary = false } =
    config || {};
  const plannerElementConfig = _.get(config, "plannerElementConfig", {}) || {};

  const unitType = _.get(
    _.find(unitPlanFields, { uid: "unitType" }),
    "value",
    ""
  );

  const {
    type,
    filteredBy,
    subType,
    isPlannerElementWithoutParent,
    rootNodeLabel,
    parentSubType,
  } = plannerElementConfig;
  const plannerElementSet =
    _.find(plannerElements, {
      type: type,
    }) || {};
  const plannerElementNodeSet =
    _.find(plannerElementNodeSets, {
      type,
    }) || {};

  let plannerElementNodes = !_.isEmpty(plannerElementSet)
    ? _.get(plannerElementSet, "nodes", [])
    : _.map(
        _.get(
          plannerElementNodeSet,
          "plannerElementNodeSetWrapperV2.publishedData.edges",
          []
        ),
        "node"
      );

  if (isCourseOverViewAndViewMode && _.isEmpty(plannerElementNodes)) {
    // use nodes from resolve minimal tree, when plannerElementSet nodes are empty, this case occurs when viewing from courseOverView with updated UI components
    plannerElementNodes = _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree.nodes",
      []
    );
  }
  let options = {};
  let parentNodes = [];
  let nodes = [];
  let value = [];
  switch (type) {
    case "MYP_ATL":
    case "UBD_ATL":
    case "DP_ATL":
    case "UBD_SKILL": {
      parentNodes = _.filter(plannerElementNodes, ({ parent }) => !parent);
      return {
        parentNodes: [
          {
            id: type,
            label: "",
          },
        ],
        plannerElementNodeSetData: {
          type,
          parentType: "PLANNER_ELEMENT",
          id: plannerElementNodeSet.id,
          groupedByType: null,

          filters: {
            grades,
          },
        },
      };
    }

    //It should be grouped by Subject Group category
    case "MYP_OBJECTIVES": {
      let filters = {};
      /*
      Handled for Interdiscplinary Criteria
    */

      if (subType == "INTERDISCIPLINARY_CRITERIA") {
        filters = {
          subTypes: [subType],
        };
        return {
          parentNodes: [
            {
              id: "INTERDISCIPLINARY_CRITERIA",
              label: "Interdisciplinary objectives",
            },
          ],
          plannerElementNodeSetData: {
            type,
            subType,
            parentType: "PLANNER_ELEMENT",
            id: plannerElementNodeSet.id,
            groupedByType: null,
            // The filters are directly consumed by plannerElementDataSet queries to fetch the data
            ...(isSinglePageAndNewUnitView() && { filters }),
          },
        };
      }
      //Handled for Subject Objectives
      else {
        const resolvedValue = _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        );

        const objectiveLevelsData =
          _.find(unitPlanFields, { uid: "objectiveLevel" }) || {};
        let selectedObjectiveLevels = _.get(objectiveLevelsData, "value", "");
        //filter out the empty objectives
        selectedObjectiveLevels = _.filter(selectedObjectiveLevels, Boolean);
        let courseDetails = {};
        if (courseId) {
          courseDetails = getCourseDetailsFromCache({
            id: courseId,
            courseGradeFilters: { academicYearId: getSelectedAcademicYearId() },
          });
        }

        parentNodes = _.map(resolvedValue, value => {
          let recommendedPhase = "";
          if (!_.isEmpty(courseDetails)) {
            const primaryGrade = _.get(courseDetails, "primaryGrade.id", {});
            const subjectRecommendedPhase = _.get(
              subjectRecommendedPhaseMap,
              value.id,
              []
            );

            recommendedPhase = _.get(
              _.find(subjectRecommendedPhase, { gradeId: primaryGrade }),
              "phaseId",
              ""
            );
          }
          return {
            id: value.id,
            label: value.name,
            recommendedPhase,
            recommendedPhaseLabel: _.find(objectiveLevelNodes, {
              id: recommendedPhase,
            })?.label,
            primaryGrade: _.get(courseDetails, "primaryGrade", {}),
            selectedObjectiveIds: _.reduce(
              selectedObjectiveLevels,
              (result, objectiveId) => {
                const objective = _.find(objectiveLevelNodes, {
                  id: objectiveId,
                });
                const subjects = getSubjectsFromDataSetNodeWrapper(objective);

                if (_.find(subjects, { id: value.id })) {
                  result.push(objectiveId);
                }
                return result;
              },
              []
            ),
          };
        });

        //For Disciplinary Subject Grounding Step, It should be only one lens of that subject group
        if (isSubjectSpecificDynamic) {
          parentNodes = _.filter(
            parentNodes,
            parentNode => parentNode.id == dynamicSubject.id
          );
        }

        // All subjects are our parent nodes
        filters = {
          subjects: _.map(parentNodes, "id"),
        };
        const isMYPObjectivePhasesEnabled = _.isEqual(
          curriculumType,
          CURRICULUM_TYPE_MYP
        )
          ? !ACLStore.can("FeatureFlag:RemoveMYPObjectivesPhase") ||
            ACLStore.can(ENABLE_MYP_PHASE_SELECTION_RECOMMENDATION)
          : true;

        let associatedSubjectNodes = parentNodes;
        // For MYP objectives, we need to filter out subjects on basis selected objective ids
        if (isMYPObjectivePhasesEnabled) {
          const {
            filteredParentNodes,
            subjectWithSelectedObjectives,
          } = _.reduce(
            parentNodes,
            (result, parentNode) => {
              const selectedObjectiveIds = parentNode.selectedObjectiveIds;
              if (!_.isEmpty(selectedObjectiveIds))
                result.subjectWithSelectedObjectives.push(parentNode);
              result.filteredParentNodes.push(...selectedObjectiveIds);
              return result;
            },
            { filteredParentNodes: [], subjectWithSelectedObjectives: [] }
          );
          // filters are used to fetch the data
          filters = {
            ...filters,
            parentNodes: filteredParentNodes,
          };
          associatedSubjectNodes = subjectWithSelectedObjectives;
        }

        const groupedByType = "SUBJECT";

        return {
          parentNodes,
          plannerElementNodeSetData: {
            type,
            groupedByType,
            id: plannerElementNodeSet.id,
            parentType: "PLANNER_ELEMENT",
            ...(isSinglePageAndNewUnitView() && {
              filters,
              associatedSubjectNodes,
            }),
            ...(isSinglePageAndNewUnitView() && shouldGroupObjectivesBySubject
              ? { parentNodes, shouldGroupObjectivesBySubject }
              : {}),
          },
          // selectedObjectiveIds
        };
      }
    }
    //It should be grouped by Subjects
    case "MYP_LEARNING_STANDARD":
    case "UBD_LEARNING_STANDARD":
    case "DP_SUBJECT_STANDARD": {
      parentNodes = _.map(
        _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        ),
        subject => {
          return {
            id: subject.id,
            label: subject.name,
            isArchived: _.get(subject, "isArchived", false),
          };
        }
      );

      //For Displinary Subject Grounding Step, It should be only one lens of that subject
      if (isSubjectSpecificDynamic) {
        parentNodes = _.filter(
          parentNodes,
          parentNode => parentNode.id == dynamicSubject.id
        );
      }
      let filters = { associatedParents: [{ ids: grades, type: "GRADE" }] };
      if (isSinglePageAndNewUnitView()) {
        // The filters are directly consumed by plannerElementDataSet queries to fetch the data, currently it will be used for v1 modal where we show old standards.
        filters = {
          associatedParents: [
            { ids: _.map(parentNodes, "id"), type: "SUBJECT" },
          ],
        };
      }

      return {
        parentNodes,
        plannerElementNodeSetData: {
          isDisciplinary,
          plannerElementSetId: _.get(
            plannerElementNodeSet,
            "plannerElementSet.id"
          ),
          type,
          parentType: "PLANNER_ELEMENT",
          groupedByType: "SUBJECT",
          filters,
          // grades for getting pre-selected grades which we will pass to the dropdown filter
          // for default selection
          // parentNodes are used for sideBarNodes & data manipulation
          ...(isSinglePageAndNewUnitView() && { parentNodes, grades }),
        },
      };
    }
    case "DP_SYLLABUS": {
      const fieldType = _.get(fieldList, `${fieldKey}.type`, "");

      switch (fieldType) {
        case "SelectDropdown": {
          const filteredPlannerElementNodes = _.filter(plannerElementNodes, {
            subType,
          });

          options = _.map(filteredPlannerElementNodes, node => {
            return { ...node, value: node.id };
          });

          return options;
        }
        default: {
          parentNodes = _.map(
            _.get(
              _.find(unitPlanFields, { uid: "subjects" }),
              "resolvedMinimalTree.subjects",
              []
            ),
            subject => {
              return { id: subject.id, label: subject.name };
            }
          );
          let filters = {
            tags: genericTags,
          };
          if (isSinglePageAndNewUnitView()) {
            // The filters are directly consumed by plannerElementDataSet queries to fetch the data
            filters = {
              subjects: _.map(parentNodes, "id"),
            };
          }

          return {
            parentNodes,
            plannerElementNodeSetData: {
              id: plannerElementNodeSet.id,
              type,
              parentType: "PLANNER_ELEMENT",
              groupedByType: "SUBJECT",
              filters,
              ...(isSinglePageAndNewUnitView() && {
                // parentNodes are used for sideBarNodes & data manipulation
                parentNodes,
                // Used for fetching subjectVariants
                subject: _.first(parentNodes),
              }),
            },
          };
        }
      }
    }

    case "DP_TOK_CONNECTION": {
      if (isSinglePageAndNewUnitView()) return plannerElementNodes;
      let parentNodes = _.filter(plannerElementNodes, { parent: null });
      parentNodes = _.map(parentNodes, parentNode => {
        const children = _.map(parentNode.children, child => {
          return _.find(plannerElementNodes, { id: child });
        });
        return {
          id: parentNode.id,
          label: parentNode.label,
          __typename: parentNode.__typename,
          children,
        };
      });
      return parentNodes;
    }
    case "MYP_KEY_CONCEPT":
      //For Interdisciplinary unit, show all Key Concepts in a single list
      if (unitType == "ibMYPInterdisciplinary") {
        return _.map(plannerElementNodes, node => {
          return { ...node, value: node.id };
        });
      } else {
        /*
        Show two lens - One is subject specific and second is others
      */
        const subjectGroupedValue = _.get(
          _.find(unitPlanFields, { uid: "subjectGroup" }),
          "value",
          []
        );

        const subjectGroupedNode = _.first(
          _.get(
            _.find(unitPlanFields, { uid: "subjectGroup" }),
            "resolvedMinimalTree.subjectGroups",
            []
          )
        );

        const subjectGroupLabel = _.get(subjectGroupedNode, "name", "");

        //Filter out subject specific options

        const subjectSpecificOptions = filteredPlannerElementNodesByProperties({
          plannerElementNodes,
          filerFieldValue: subjectGroupedValue,
          key: "subjectGroups",
        });

        //Filter out other options
        const otherOptions = _.differenceBy(
          plannerElementNodes,
          subjectSpecificOptions,
          "id"
        );

        //Adding only those lens in which options are not empty
        parentNodes = [];
        if (!_.isEmpty(subjectSpecificOptions)) {
          parentNodes.push({
            id: "SUBJECT_SPECIFIC",
            label: t("common:concepts_with_subject_group_label", {
              subjectGroup: subjectGroupLabel,
            }),
            children: _.map(subjectSpecificOptions, node => {
              // To maintain consistency with other fields i.e. making flat structure we will only pass ids in the children &
              // Children Nodes will be pushed seperately
              return isSinglePageAndNewUnitView()
                ? node.id
                : { ...node, value: node.id };
            }),
          });
          if (isSinglePageAndNewUnitView()) {
            parentNodes.push(
              ..._.map(subjectSpecificOptions, node => {
                return { ...node, value: node.id, parent: "SUBJECT_SPECIFIC" };
              })
            );
          }
        }
        if (!_.isEmpty(otherOptions)) {
          parentNodes.push({
            id: "OTHERS",
            label: t("common:other_subject_group_key_concepts_label"),
            children: _.map(otherOptions, node => {
              return isSinglePageAndNewUnitView()
                ? node.id
                : { ...node, value: node.id };
            }),
          });
          if (isSinglePageAndNewUnitView()) {
            parentNodes.push(
              ..._.map(otherOptions, node => {
                return { ...node, value: node.id, parent: "OTHERS" };
              })
            );
          }
        }

        return parentNodes;
      }
    case "MYP_RELATED_CONCEPT": {
      parentNodes = _.map(
        _.get(
          _.find(unitPlanFields, { uid: "subjects" }),
          "resolvedMinimalTree.subjects",
          []
        ),
        subject => {
          return {
            id: subject.id,
            label: subject.name,
            isArchived: _.get(subject, "isArchived", false),
          };
        }
      );
      //For Displinary Subject Grounding Step, It should be only one lens of that subject
      if (isSubjectSpecificDynamic) {
        parentNodes = _.filter(
          parentNodes,
          parentNode => parentNode.id == dynamicSubject.id
        );
      }
      const filters = {
        subjects: _.map(parentNodes, "id"),
      };
      return {
        parentNodes,
        plannerElementNodeSetData: {
          type,
          parentType: "PLANNER_ELEMENT",
          id: plannerElementNodeSet.id,
          groupedByType: "SUBJECT",
          // The filters are directly consumed by plannerElementDataSet queries to fetch the data
          ...(isSinglePageAndNewUnitView() && { filters, parentNodes }),
        },
      };
    }
    case "MYP_SPECIFIED_CONCEPTS": {
      const dummyRootNode = getDummyRootNode({
        t,
        type,
        rootNodeLabel,
        rootNodes: _.reduce(
          nodes,
          (result, node) => {
            if (node.depth === 1) result.push(node.id);
            return result;
          },
          []
        ),
      });

      // used for default selection of filters
      const subjectGroups = _.get(
        _.find(unitPlanFields, { uid: "subjectGroup" }),
        "value"
      );

      return {
        parentNodes: [dummyRootNode],
        plannerElementNodeSetData: {
          type,
          parentType: "PLANNER_ELEMENT",
          id: plannerElementNodeSet.id,
          subjectGroups,
        },
      };
    }
    case "DP_AIM":
    case "DP_OBJECTIVES":
      /*
      we are creating a union of nodes here beacause if parent is removed based on filters than we can still display the selected
      nodes
      Eg : if we have selected a HL aim and removed HL from the subject level than we wont get the HL nodes in Planner elements
      so if any of the HL node is selected we can fetch it from resolved minimal tree
      */
      nodes = getPlannerElementNodesWithSelectedValues({
        plannerElementNodes,
        unitPlanFields,
        fieldKey,
      });
      if (isSinglePageAndNewUnitView()) {
        return nodes;
      }
      parentNodes = _.reduce(
        nodes,
        (result, node) => {
          const associatedParents = _.map(node.associatedParents, parent => {
            return { id: parent.id, label: parent.name };
          });

          result = _.unionBy(result, associatedParents, "id");
          return result;
        },
        []
      );

      value = _.reduce(
        nodes,
        (result, node) => {
          const associatedParents = _.map(node.associatedParents, parent => {
            return parent.id;
          });
          if (
            _.find(parentNodes, parentNode => {
              return _.includes(associatedParents, parentNode.id);
            })
          ) {
            result.push(node.id);
          }
          return result;
        },
        []
      );

      /*
        description of options for nestedCHecklist
        parent nodes are the nodes which will be at depth 0
        values are the id of the nodes which we are displaying as options
        nodes will store the details of the values
      */

      return {
        parentNodes,
        value,
        nodes,
        plannerElementNodeSetData: {
          id: plannerElementNodeSet.id,
          type,
          parentType: "PLANNER_ELEMENT",
          groupedByType: "SUBJECT",
          filters: {
            tags: genericTags,
          },
        },
      };

    case "DP_CAS_APPROACHES": {
      options = _.map(
        _.filter(plannerElementNodes, node =>
          _.some(node.associatedParents, item =>
            _.isEqual(item?.metadata?.subType, parentSubType)
          )
        ),
        node => {
          return { ...node, value: node.id };
        }
      );
      return options;
    }
    case "DP_CAS_LEARNING_OUTCOMES": {
      options = _.map(plannerElementNodes, node => {
        // attach display name to each LO's
        const displayName = _.get(node, ["metadata", "display_name"], "");
        const updatedLabel = _.includes(node?.label, displayName)
          ? node?.label
          : `${displayName}: ${node?.label}`;

        return { ...node, value: node.id, label: updatedLabel };
      });

      return options;
    }

    default: {
      if (isPlannerElementWithoutParent) {
        /**
         * When ever we want to use the SNS editor where there is no parent associated to the plannerelement node
         * we need to pass a boolean for that planner element isPlannerElementWithoutParent so this will add a dummy
         * root node to the list of nodes
         */
        nodes = getPlannerElementNodesWithSelectedValues({
          plannerElementNodes,
          unitPlanFields,
          fieldKey,
        });
        const dummyRootNode = getDummyRootNode({
          t,
          type,
          rootNodeLabel,
          rootNodes: _.reduce(
            nodes,
            (result, node) => {
              if (node.depth === 1) result.push(node.id);
              return result;
            },
            []
          ),
        });
        parentNodes = [dummyRootNode];
        nodes = [dummyRootNode, ...nodes];
        value = _.reduce(
          nodes,
          (result, node) => {
            const associatedParents = [node.parent];
            if (
              _.find(parentNodes, parentNode => {
                return _.includes(associatedParents, parentNode.id);
              })
            ) {
              result.push(node.id);
            }
            return result;
          },
          []
        );
        nodes = _.map(nodes, node => {
          if (_.isEmpty(node.parent))
            return {
              ...node,
              parent: -1,
            };
          return node;
        });
        /*
          description of options for nestedCHecklist
          parent nodes are the nodes which will be at depth 0
          values are the id of the nodes which we are displaying as options
          nodes will store the details of the values
        */
        return {
          parentNodes,
          value,
          nodes,
          dummyRootNode,
          plannerElementNodeSetData: {
            id: plannerElementNodeSet.id,
            type,
            parentType: "PLANNER_ELEMENT",
            groupedByType: type,
          },
        };
      }

      /*
    Handled If you want to filtered nodes in the frontend
    E.g - Focus Areas are filtered by Global Context, Related Key Concepts are filtered by Subject Group
  */
      if (!_.isEmpty(filteredBy)) {
        _.map(filteredBy, filterKey => {
          const filerFieldValue = _.get(
            _.find(unitPlanFields, { uid: filterKey }),
            "value",
            []
          );

          plannerElementNodes = filteredPlannerElementNodesByProperties({
            plannerElementNodes,
            filerFieldValue,
          });
        });
      }

      const fieldType = _.get(fieldObj, "type", "");

      const isGenericCard = _.isEqual(fieldType, "GenericCardList");

      if (_.isEqual(type, "DP_DIFFERENTIATION_APPROACH") && isGenericCard) {
        const fieldValue = _.get(
          _.find(unitPlanFields, { uid: fieldKey }),
          "value",
          []
        );

        return _.map(fieldValue, item => {
          return {
            ...item,
            label: item.label || item.value,
          };
        });
      }

      options = _.map(plannerElementNodes, node => {
        return { ...node, value: node.id };
      });

      return options;
    }
  }
};

//if the selected node is removed from the planner elements still we will add it from here so that user will be able to remove it
const getPlannerElementNodesWithSelectedValues = ({
  plannerElementNodes,
  unitPlanFields,
  fieldKey,
}) => {
  return _.unionBy(
    plannerElementNodes,
    _.get(
      _.find(unitPlanFields, { uid: fieldKey }),
      "resolvedMinimalTree.nodes",
      []
    ),
    "id"
  );
};

const getDummyRootNode = ({ rootNodeLabel, rootNodes, type, t }) => {
  return {
    id: -1,
    name: t(rootNodeLabel),
    depth: 0,
    type,
    children: rootNodes,
    isDummy: true,
  };
};

const filteredPlannerElementNodesByProperties = ({
  plannerElementNodes,
  filerFieldValue,
  key = "associatedPlannerElementNodes",
}) => {
  return _.filter(plannerElementNodes, node => {
    const ids = _.map(
      _.get(node, "dataSetNodeWrapper.properties." + key, []),
      "id"
    );

    if (!!filerFieldValue && !_.isArray(filerFieldValue)) {
      filerFieldValue = [filerFieldValue];
    }

    return !_.isEmpty(_.intersection(ids, filerFieldValue));
  });
};

const getUnitTypeOptions = ({
  organizationId,
  curriculumType,
  t,
  sendAllOption = false,
}) => {
  // const unitTypes = _.uniqBy(
  //   _.filter(
  //     _.get(getUniqlyCombinedGradesDetails(grades), "plannerTemplates", []),
  //     template => template.type == "UNIT_PLAN" && !!template.unitType
  //   ),
  //   "unitType"
  // );

  //Get unittypes from organization

  const unitTypes = _.filter(
    _.get(getOrganizationDetailsFromCache(organizationId), "unitTypes", []),
    unitType => _.get(unitType, "curriculum.type", "IB_PYP") == curriculumType
  );

  let unitTypeOptions = _.map(unitTypes, unitType => {
    return {
      label: t ? t(UNIT_TYPE_LOCALE[unitType.id]) : unitType.name,
      value: unitType.id,
      key: unitType.id,
    };
  });

  if (sendAllOption) {
    unitTypeOptions = _.concat(
      [{ label: t("common:all"), key: "ALL" }],
      unitTypeOptions
    );
  }

  return unitTypeOptions;
};

export const getUnitTypeOptionsMemoized = getMemoizedLangFunction(
  getUnitTypeOptions
);

export const getCombinedGradeDetails = grades => {
  const gradeList = getTemplateBasicDetailsOfGradeFromCache({ grades });
  return uniquelyCombineGradeDetails(gradeList);
};

export const getPlannerTypeOptions = ({
  grades,
  unitType,
  curriculumType,
  selectedSubjects,
}) => {
  const gradeDetails = getCombinedGradeDetails(grades);

  let plannerTemplates = _.filter(
    _.get(gradeDetails, "plannerTemplates", []),
    template =>
      template.type == "UNIT_PLAN" &&
      _.get(template, "curriculum.type", "IB_PYP") == curriculumType &&
      (template.unitType == unitType || !template.unitType) //Handled for those templates which are independent from unit type
  );

  //In case of DP we need to fetch the planner types based on the subject type so we are adding a additional filter in case of DP
  if (curriculumType == CURRICULUM_TYPE_DP && !_.isEmpty(selectedSubjects)) {
    const subjectType = _.get(
      _.find(gradeDetails.subjects, sub =>
        _.includes(selectedSubjects?.value, sub.id)
      ),
      "type",
      SUBJECT_TYPE_GENERIC
    );
    plannerTemplates = _.filter(plannerTemplates, template => {
      if (subjectType != SUBJECT_TYPE_GENERIC)
        return template.subType == subjectType;
      return _.isEmpty(template.subType);
    });
  }

  return _.map(plannerTemplates, template => {
    return { label: template.name, value: template.id };
  });
};

const getAtlsOptions = ({
  grades,
  unitPlanFields,
  readOnly,
  plannerElementNodeSets,
}) => {
  const atlCategories = getATLCategoriesList({
    grades,
    unitPlanFields,
    readOnly,
  });
  if (isSinglePageAndNewUnitView()) {
    const plannerElementNodeSet =
      _.find(plannerElementNodeSets, {
        type: ELEMENT_TYPE_PYP_ATL,
      }) || {};

    return {
      // parentNodes are used in the middlePanel to show disabled nodes
      parentNodes: [
        {
          id: ELEMENT_TYPE_PYP_ATL,
          label: "",
        },
      ],
      // To maintain consistency with other fields we are making plannerElementNodeSetData,
      // Grades are passed to fetch data
      plannerElementNodeSetData: {
        id: plannerElementNodeSet.id,
        type: ELEMENT_TYPE_PYP_ATL,
        grades,
      },
    };
  }
  return {
    grades,
    atlCategories,
  };
};

const getHoursOptions = () => {
  return _.map(_.range(1, 100), i => ({
    value: i,
    label: `${i} hour${i > 1 ? "s" : ""}`,
  }));
};

const getElementOptions = ({
  uid,
  unitPlanFields,
  readOnly,
  plannerElements,
  plannerElementNodeSets,
  pypPlannerElementConfig,
  fieldObj,
}) => {
  const type = _.get(pypPlannerElementConfig, "type", "");
  const setKey = pypElementSetMapping[uid];

  let combineNodes = [];

  if (!readOnly) {
    const elementSet = _.find(plannerElements, { type }) || {};
    const elementNodeSet = _.find(plannerElementNodeSets, { type }) || {};
    const hasRootNodes = _.has(elementSet, "rootNodes");
    const nodes = !_.isEmpty(elementSet)
      ? _.get(elementSet, "nodes")
      : _.map(
          _.get(
            elementNodeSet,
            "plannerElementNodeSetWrapperV2.publishedData.edges",
            []
          ),
          "node"
        );

    if (hasRootNodes) {
      const rootNodes = _.get(elementSet, "rootNodes", []);

      _.forEach(rootNodes, (nodeId, index) => {
        const element = _.find(nodes, { id: nodeId });
        if (!_.isEmpty(element)) {
          combineNodes.push(element);
        }
      });
    } else {
      combineNodes = [...combineNodes, ...(nodes || [])];
    }
  }

  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: uid }),
    "resolvedMinimalTree",
    {}
  );
  const resolvedNodes = _.get(resolvedValue, setKey, []);
  combineNodes = _.unionBy([...combineNodes, ...resolvedNodes], "id");

  // combining concepts and action fields with their respective questions fields
  const subFieldConfig = _.get(fieldObj, "subField.config", {});

  const elements = [];
  _.forEach(combineNodes, element => {
    if (!_.isEmpty(element)) {
      elements.push({
        node: element,
        label: element.label,
        metadata: element.metadata,
        value: element.id,
        // cases like uid = theme have descriptors, so we need to show them in subText
        subText: element.subText || element.descriptor,
        icon: _.get(element, "iconUrl") || _.get(element, "metadata.icon"),
        id: element.id,
        ...subFieldConfig,
      });
    }
  });

  return elements;
};

const getElementQuesOptions = ({
  organizationId,
  fieldObj,
  unitPlanFields,
  uid,
}) => {
  const fieldOptions = _.get(fieldObj, "config.options", {});
  const cacheKey = pypElementMapping[uid];
  const setKey = pypElementSetMapping[uid];
  // let pypConstants = getOrganizationPypElementSetFromCache({
  //   organizationId,
  //   type: cacheKey
  // });
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: uid }),
    "resolvedMinimalTree",
    {}
  );

  const nodes = _.get(resolvedValue, setKey, []); //_.get(pypConstants, `pypElement.${setKey}`, {});

  const selectedElement = _.get(
    _.find(unitPlanFields, { uid: uid }),
    "value",
    ""
  );
  const elements = [];
  _.forEach(selectedElement, (nodeId, index) => {
    const element = _.find(nodes, { id: nodeId });
    if (element) {
      elements.push({
        label: element.label,
        value: element.id,
        subText: element.subText,
      });
    }
  });
  const extraText = "";

  return { ...fieldOptions, nodes: elements, extraText };
};

const getFocusAreaOptions = ({
  unitPlanFields,
  readOnly,
  plannerElements,
  plannerElementNodeSets,
  pypPlannerElementConfig,
}) => {
  const themes = getElementOptions({
    uid: "theme",
    unitPlanFields,
    readOnly,
    plannerElements,
    plannerElementNodeSets,
    pypPlannerElementConfig,
  });
  const id = _.get(_.find(unitPlanFields, { uid: "theme" }), "id", "");
  const responseFilter = getUnitPlanFieldResponseFilter();

  const theme =
    getUnitPlanFieldFromCache({
      fieldId: id,
      responseFilter,
    }).value || themes[0]?.value;

  const focusAreas = _.get(
    _.find(themes, item => _.get(item, "node.id", "") == theme),
    "node.associatedChildren",
    []
  );
  return _.map(focusAreas, obj => {
    return { label: obj.label, value: obj.id, id: obj.id };
  });
};

const getSubjectOptions = ({ subjects }) => {
  return _.map(subjects, (subject, index) => {
    return {
      node: subject,
      label: subject.name,
      value: subject.id,
      benchmarkRootNode: subject.benchmarkRootNode,
      isArchived: _.get(subject, "isArchived", false),
    };
  });
};

const getTeacherQuestionsOptions = ({ unitPlanFields }) => {
  const id = _.get(_.find(unitPlanFields, { uid: "lois" }), "id", "");
  const responseFilter = getUnitPlanFieldResponseFilter();

  return _.filter(
    getUnitPlanFieldFromCache({ fieldId: id, responseFilter }).value,
    item => _.trim(item.value) != ""
  );
};

const getSubjectList = ({
  grades,
  readOnly,
  unitPlanFields,
  learningCourseId,
  academicYearId,
}) => {
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "resolvedMinimalTree",
    ""
  );

  let subjects = [];
  if (!readOnly) {
    //For MYP Fetch Subjects from Course
    //uniquely combined subjects from gradeDetailsQuery
    subjects = _.get(getUniquelyCombinedGradesDetails(grades), "subjects", []);
  }
  if (!_.isEmpty(learningCourseId)) {
    const learningCourseSubjects = getLearningCourseSubjects({
      academicYearId,
      learningCourseId,
    });
    const filteredSubjects = _.intersectionBy(
      subjects,
      learningCourseSubjects,
      "id"
    );
    return _.uniqBy(
      [...filteredSubjects, ..._.get(resolvedValue, "subjects", [])],
      "id"
    );
  }

  return _.uniqBy([...subjects, ..._.get(resolvedValue, "subjects", [])], "id");
};

const getATLCategoriesList = ({ grades, readOnly, unitPlanFields }) => {
  let atlCategories = [];
  if (!readOnly) {
    atlCategories = _.get(
      getUniquelyCombinedGradesDetails(grades),
      "atlCategories",
      []
    );
  }
  const resolvedValue = _.get(
    _.find(unitPlanFields, { uid: "atls" }),
    "resolvedMinimalTree",
    []
  );

  return _.uniqBy(
    [
      ...atlCategories,
      ..._.filter(_.get(resolvedValue, "atls", []), item => !item.parent),
    ],
    "id"
  );
};

const getBenchmarksOptions = ({ grades, unitPlanFields }) => {
  const subjects = getSubjectList({ grades, readOnly: true, unitPlanFields });

  if (isSinglePageAndNewUnitView()) {
    return {
      // parentNodes are used in middlePane to detect if we wanna show disabled state
      parentNodes: subjects,
      plannerElementNodeSetData: {
        type: ELEMENT_TYPE_PYP_LEARNING_STANDARD,
        grades,
        subjects: _.map(subjects, subject => {
          return {
            ...subject,
            label: subject.name,
          };
        }),
        parentNodes: subjects,
      },
    };
  }
  const subjectList = getSubjectOptions({ subjects });
  return {
    subjects: subjectList,
    grades,
  };
};

const getRelatedConceptOptions = ({
  unitPlanFields,
  defaultOptions,
  plannerElementNodeSets,
}) => {
  const selectdSubject = _.get(
    _.find(unitPlanFields, { uid: "subjects" }),
    "value",
    ""
  );

  const selectedConcepts = _.get(
    _.find(unitPlanFields, { uid: "concepts" }),
    "value",
    ""
  );

  const groupedOptionsFields = _.get(defaultOptions, "groupedOptionsFields", [
    "subjects",
    "concepts",
  ]);
  const plannerElementNodeSetId = _.get(
    _.find(plannerElementNodeSets, {
      type: "PYP_RELATED_CONCEPT",
    }),
    "id"
  );

  return {
    plannerElementNodeSetId,
    subjects: selectdSubject ? selectdSubject : [],
    concepts: selectedConcepts ? selectedConcepts : [],
    groupedOptionsFields: groupedOptionsFields ? groupedOptionsFields : [],
  };
};

export const getRelatedConceptsGroupedValueOptions = ({
  nodes,
  subjects,
  value,
  concepts,
  groupedOptionsFields,
}) => {
  const groupedRelatedConcepts = [];
  const groupedOptions = {};
  const groupedValue = {};
  const subjectRelatedConceptSets = [];
  const conceptRelatedConceptSets = [];
  _.forEach(groupedOptionsFields, fields => {
    switch (fields) {
      case "subjects":
        _.forEach(subjects, subject => {
          const children = _.filter(
            nodes,
            node =>
              node.elementId == subject &&
              node.elementType == "SUBJECT" &&
              node.parent
          );
          const currentOptions = [];
          const currentValue = [];
          if (children.length > 0) {
            _.forEach(children, child => {
              if (child) {
                currentOptions.push({
                  label: child.label,
                  value: child.id,
                });
              }
            });
            _.forEach(value, val => {
              const node = _.find(nodes, { id: val });
              if (_.get(node, "elementId", "") == subject) {
                currentValue.push({
                  label: node.label,
                  value: node.id,
                });
              }
            });
          }

          const subjectBasicDetails = getSubjectLabelAndIsArchived(subject);
          const label = _.get(subjectBasicDetails, "name", "");
          const isArchived = _.get(subjectBasicDetails, "isArchived", false);
          if (label) {
            const id = `SUBJECT:${subject}`;
            subjectRelatedConceptSets.push({
              id,
              label,
              isArchived,
            });
            groupedOptions[id] = currentOptions;
            groupedValue[id] = currentValue;
          }
        });
        groupedRelatedConcepts.push({
          id: "SUBJECT",
          label: "Subjects",
          relatedConceptSets: subjectRelatedConceptSets,
        });
        break;
      case "concepts":
        _.forEach(concepts, concept => {
          const children = _.filter(
            nodes,
            node =>
              node.elementId == concept &&
              node.elementType == "CONCEPT" &&
              node.parent
          );

          const currentOptions = [];
          const currentValue = [];
          if (children.length > 0) {
            _.forEach(children, child => {
              if (child) {
                currentOptions.push({ label: child.label, value: child.id });
              }
            });
            _.forEach(value, val => {
              const node = _.find(nodes, { id: val });
              if (_.get(node, "elementId", "") == concept) {
                currentValue.push({
                  label: node.label,
                  value: node.id,
                });
              }
            });
          }

          const label = getElementLabel({ type: "CONCEPT", value: concept });
          if (label) {
            const id = `CONCEPT:${concept}`;
            conceptRelatedConceptSets.push({
              id,
              label,
            });
            groupedOptions[id] = currentOptions;
            groupedValue[id] = currentValue;
          }
        });

        groupedRelatedConcepts.push({
          id: "CONCEPT",
          label: "Key Concepts",
          relatedConceptSets: conceptRelatedConceptSets,
        });
        break;
    }
  });

  return { groupedRelatedConcepts, groupedValue, groupedOptions };
};

export const getIsBulkImportV2Enabled = () => {
  return ACLStore.can("FeatureFlag:UnifyUnitImport");
};

export const getIsImportFromAnotherCurriculumEnabled = () => {
  return ACLStore.can("FeatureFlag:AllowOtherCurriculumUnitImport");
};

const CREATE_PLANNER_RESOURCE_INPUT = [
  "title",
  "type",
  "description",
  "createdBy",
  "parentType",
  "parentId",
  "attachments",
];

const UPDATE_PLANNER_RESOURCE_INPUT = [
  "resourceId",
  "title",
  "type",
  "description",
  "updatedBy",
  "attachments",
  "isForced",
];

export const unitTypeLabels = {
  [MYP_SUBJECT_SPECIFIC]: "common:subject_specific",
  [MYP_INTERDISCIPLINARY]: "common:interdisciplinary",
  [DP_SUBJECT_SPECIFIC]: "common:subject_specific",
  [PYP_TRANSDISCIPLINARY]: "common:transdisciplinary",
  [PYP_SUBJECT_SPECIFIC]: "common:subject_specific",
  [UBD_INQUIRY]: "common:interdisciplinary",
  [UBD_SUBJECT_SPECIFIC]: "common:subject_specific",
};

// Auto-generated exports
export {
  fieldUids,
  getUnitPlanCourses,
  getNonPlannerFieldOptions,
  getPlannerElementNodesWithSelectedValues,
  getDummyRootNode,
  getUnitTypeOptions,
  getAtlsOptions,
  getHoursOptions,
  getElementQuesOptions,
  getFocusAreaOptions,
  getSubjectOptions,
  getTeacherQuestionsOptions,
  getSubjectList,
  getATLCategoriesList,
  getBenchmarksOptions,
  CREATE_PLANNER_RESOURCE_INPUT,
  UPDATE_PLANNER_RESOURCE_INPUT,
};
